import { DealsAnalyticsContext, DepartmentPlanDeal } from '../../../model/dashboard.types'

export type AnalyticsMetric = 'opportunity' | 'profit'

export type DealAnalyticsPoint = {
  date: string
  label: string
  opportunityValue: number | null
  profitValue: number | null
  opportunityPct: number | null
  profitPct: number | null
  idealPct: number
  opportunityForecastValue: number | null
  profitForecastValue: number | null
  opportunityForecastPct: number | null
  profitForecastPct: number | null
}

const DAY = 86_400_000
const round = (value: number) => Number(value.toFixed(2))
const utc = (value: string) => new Date(`${value.slice(0, 10)}T00:00:00Z`).getTime()
const iso = (value: number) => new Date(value).toISOString().slice(0, 10)

export function todayIso(now = new Date()): string {
  return new Intl.DateTimeFormat('sv-SE', {
    timeZone: 'Europe/Moscow', year: 'numeric', month: '2-digit', day: '2-digit'
  }).format(now)
}

function chartDates(start: string, end: string): string[] {
  const startMs = utc(start), endMs = utc(end)
  if (!Number.isFinite(startMs) || !Number.isFinite(endMs) || endMs < startMs) return []
  const totalDays = Math.floor((endMs - startMs) / DAY) + 1
  const step = Math.max(1, Math.ceil(totalDays / 190))
  const result: string[] = []
  for (let index = 0; index < totalDays; index += step) result.push(iso(startMs + index * DAY))
  if (result[result.length - 1] !== end.slice(0, 10)) result.push(end.slice(0, 10))
  return result
}

export function buildDealAnalytics(
  deals: DepartmentPlanDeal[],
  context: DealsAnalyticsContext,
  now = new Date()
) {
  const start = context.dateStart.slice(0, 10)
  const end = context.dateEnd.slice(0, 10)
  let dates = chartDates(start, end)
  const startMs = utc(start), endMs = utc(end)
  const totalDays = Math.max(1, Math.floor((endMs - startMs) / DAY) + 1)
  const today = todayIso(now)
  const cutoff = today < start ? start : (today > end ? end : today)
  if (!dates.includes(cutoff)) dates = [...dates, cutoff].sort()
  const cutoffMs = utc(cutoff)
  const sorted = deals
    .filter(deal => deal.invoice_date >= start && deal.invoice_date <= end)
    .slice()
    .sort((a, b) => a.invoice_date.localeCompare(b.invoice_date) || a.id - b.id)
  let opportunity = 0, profit = 0, pointer = 0
  const points: DealAnalyticsPoint[] = dates.map(date => {
    const dateMs = utc(date)
    while (pointer < sorted.length && sorted[pointer].invoice_date <= date) {
      opportunity += Number(sorted[pointer].opportunity || 0)
      profit += Number(sorted[pointer].profit || 0)
      pointer += 1
    }
    const elapsedDays = Math.min(totalDays, Math.max(1, Math.floor((dateMs - startMs) / DAY) + 1))
    const idealPct = round(elapsedDays / totalDays * 100)
    const isObserved = dateMs <= cutoffMs
    const opportunityPct = context.opportunityTarget > 0 ? round(opportunity / context.opportunityTarget * 100) : 0
    const profitPct = context.profitTarget > 0 ? round(profit / context.profitTarget * 100) : 0
    return {
      date,
      label: new Date(`${date}T12:00:00Z`).toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit' }),
      opportunityValue: isObserved ? round(opportunity) : null,
      profitValue: isObserved ? round(profit) : null,
      opportunityPct: isObserved ? opportunityPct : null,
      profitPct: isObserved ? profitPct : null,
      idealPct,
      opportunityForecastValue: isObserved ? round(opportunity / (elapsedDays / totalDays)) : null,
      profitForecastValue: isObserved ? round(profit / (elapsedDays / totalDays)) : null,
      opportunityForecastPct: isObserved ? round(opportunityPct / (elapsedDays / totalDays)) : null,
      profitForecastPct: isObserved ? round(profitPct / (elapsedDays / totalDays)) : null,
    }
  })
  const observed = points.filter(point => point.opportunityPct != null)
  const current = observed[observed.length - 1] ?? points[0]
  const completed = today >= end
  const finalOpportunityPct = context.opportunityTarget > 0
    ? round(sorted.reduce((sum, deal) => sum + Number(deal.opportunity || 0), 0) / context.opportunityTarget * 100) : 0
  const finalProfitPct = context.profitTarget > 0
    ? round(sorted.reduce((sum, deal) => sum + Number(deal.profit || 0), 0) / context.profitTarget * 100) : 0
  const observedDeals = sorted.filter(deal => deal.invoice_date <= cutoff)
  const responsible = new Map<string, { name: string; opportunity: number; profit: number; deals: number }>()
  const clients = new Map<string, number>()
  observedDeals.forEach(deal => {
    const item = responsible.get(deal.responsible) ?? { name: deal.responsible, opportunity: 0, profit: 0, deals: 0 }
    item.opportunity += Number(deal.opportunity || 0); item.profit += Number(deal.profit || 0); item.deals += 1
    responsible.set(deal.responsible, item)
    const client = deal.client?.trim() || 'Без клиента'
    clients.set(client, (clients.get(client) ?? 0) + Number(deal.opportunity || 0))
  })
  const totalOpportunity = observedDeals.reduce((sum, deal) => sum + Number(deal.opportunity || 0), 0)
  const totalProfit = observedDeals.reduce((sum, deal) => sum + Number(deal.profit || 0), 0)
  const totalMarginProfit = observedDeals.reduce((sum, deal) => sum + Number(deal.margin_profit || 0), 0)
  const totalPaid = observedDeals.reduce((sum, deal) => sum + Number(deal.actual_paid || 0), 0)
  const clientRows = Array.from(clients).map(([name, value]) => ({ name, value })).sort((a, b) => b.value - a.value)
  const shipmentDates = Array.from(new Set(observedDeals.map(deal => deal.invoice_date))).sort()
  const shipmentGaps = shipmentDates.slice(1).map((date, index) => Math.floor((utc(date) - utc(shipmentDates[index])) / DAY))
  const lastShipmentDate = shipmentDates[shipmentDates.length - 1] ?? null
  const daysSinceLastShipment = lastShipmentDate ? Math.max(0, Math.floor((cutoffMs - utc(lastShipmentDate)) / DAY)) : null
  const averageGapDays = shipmentGaps.length
    ? round(shipmentGaps.reduce((sum, value) => sum + value, 0) / shipmentGaps.length)
    : null
  const longestGapDays = Math.max(daysSinceLastShipment ?? 0, ...shipmentGaps, 0)
  const elapsedDays = Math.min(totalDays, Math.max(1, Math.floor((cutoffMs - startMs) / DAY) + 1))
  const activeElapsedDays = lastShipmentDate
    ? Math.max(1, Math.floor((utc(lastShipmentDate) - startMs) / DAY) + 1)
    : elapsedDays
  const continuityFactor = elapsedDays / activeElapsedDays
  const opportunityValues = observedDeals.map(deal => Number(deal.opportunity || 0)).sort((a, b) => a - b)
  const medianDeal = opportunityValues.length
    ? (opportunityValues.length % 2
      ? opportunityValues[Math.floor(opportunityValues.length / 2)]
      : (opportunityValues[opportunityValues.length / 2 - 1] + opportunityValues[opportunityValues.length / 2]) / 2)
    : 0
  const lowMarginDeals = observedDeals.filter(deal => Number(deal.marginality ?? 0) > 0 && Number(deal.marginality) < 15).length
  const negativeProfitDeals = observedDeals.filter(deal => Number(deal.profit || 0) < 0 || Number(deal.margin_profit || 0) < 0).length
  return {
    points,
    current,
    completed,
    finalOpportunityPct,
    finalProfitPct,
    totalDays,
    elapsedDays,
    dealsCount: observedDeals.length,
    totalOpportunity,
    totalProfit,
    totalPaid,
    marginality: totalOpportunity > 0 ? round(totalMarginProfit / totalOpportunity * 100) : 0,
    grossProfitRate: totalOpportunity > 0 ? round(totalProfit / totalOpportunity * 100) : 0,
    paymentRate: totalOpportunity > 0 ? round(totalPaid / totalOpportunity * 100) : 0,
    averageDeal: observedDeals.length ? totalOpportunity / observedDeals.length : 0,
    medianDeal,
    lowMarginDeals,
    negativeProfitDeals,
    shipmentDays: shipmentDates.length,
    lastShipmentDate,
    daysSinceLastShipment,
    averageGapDays,
    longestGapDays,
    continuityOpportunityPct: context.opportunityTarget > 0 ? round(totalOpportunity * continuityFactor / context.opportunityTarget * 100) : 0,
    continuityProfitPct: context.profitTarget > 0 ? round(totalProfit * continuityFactor / context.profitTarget * 100) : 0,
    topClientShare: totalOpportunity > 0 ? round((clientRows[0]?.value ?? 0) / totalOpportunity * 100) : 0,
    responsible: Array.from(responsible.values()).sort((a, b) => b.opportunity - a.opportunity).slice(0, 12),
    clients: clientRows.slice(0, 10),
    dealQuality: observedDeals.slice().sort((a, b) => Number(b.opportunity) - Number(a.opportunity)).slice(0, 10).map(deal => ({
      id: deal.id,
      name: `#${deal.id}`,
      opportunity: Number(deal.opportunity || 0),
      profit: Number(deal.profit || 0),
      marginality: Number(deal.marginality || 0),
    })),
  }
}

export function metricValue(point: DealAnalyticsPoint, metric: AnalyticsMetric) {
  return metric === 'opportunity' ? point.opportunityPct : point.profitPct
}

export function forecastValue(point: DealAnalyticsPoint, metric: AnalyticsMetric) {
  return metric === 'opportunity' ? point.opportunityForecastPct : point.profitForecastPct
}

export function metricMoneyValue(point: DealAnalyticsPoint, metric: AnalyticsMetric) {
  return metric === 'opportunity' ? point.opportunityValue : point.profitValue
}

export function forecastMoneyValue(point: DealAnalyticsPoint, metric: AnalyticsMetric) {
  return metric === 'opportunity' ? point.opportunityForecastValue : point.profitForecastValue
}

export function continuityValue(
  analytics: ReturnType<typeof buildDealAnalytics>,
  metric: AnalyticsMetric
) {
  return metric === 'opportunity' ? analytics.continuityOpportunityPct : analytics.continuityProfitPct
}
